import { useState, useEffect } from "react";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import { 
  Users, Search, Plus, MoreHorizontal, 
  CheckCircle, XCircle, UserX, UserCheck, Trash2, Edit2, AlertTriangle,
  Shield, Briefcase, Palette, RefreshCw, Mail
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const ROLES = [
  { id: "COO", label: "COO / Admin", desc: "Full system access", category: "admin" },
  { id: "Program Manager", label: "Program Manager", desc: "Manages assigned programs", category: "pm" },
  { id: "Marketing PM", label: "Marketing PM", desc: "Marketing-focused PM", category: "pm" },
  { id: "Production", label: "Production", desc: "Studio scheduling", category: "admin" },
  { id: "Sales", label: "Sales", desc: "Intake & clients", category: "admin" },
  { id: "Artist", label: "Artist", desc: "View assigned work", category: "artist" },
  { id: "Accountant", label: "Accountant/HR", desc: "Financial access", category: "admin" },
  { id: "VEXOS Executive", label: "VEXOS Executive", desc: "VEXOS Executive dashboard + Portfolio only", category: "admin" },
  { id: "Sales Assistant", label: "Sales Assistant", desc: "Client Calculator + Upsells only", category: "admin" },
];

const ADMIN_ROLES = ["COO", "Production", "Sales", "Accountant", "VEXOS Executive", "Sales Assistant"];
const PM_ROLES = ["Program Manager", "Marketing PM"];
const ARTIST_ROLES = ["Artist"];

function StatusBadge({ status }) {
  const configs = {
    active: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
    invited: "bg-amber-500/10 text-amber-600 border-amber-500/20",
    pending: "bg-blue-500/10 text-blue-600 border-blue-500/20",
    disabled: "bg-red-500/10 text-red-600 border-red-500/20",
  };
  const labels = { active: "Active", invited: "Invited", pending: "Pending", disabled: "Disabled" };
  return <Badge variant="outline" className={configs[status]}>{labels[status]}</Badge>;
}

function UserRow({ u, onApprove, onReject, onEdit, onDisable, onEnable, onDelete, onResendInvite, onRevokeInvite }) {
  const isPending = u.status === "pending";
  const isInvited = u.status === "invited";
  
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 hover:bg-muted/30 transition-colors" data-testid={`user-row-${u.id}`}>
      <div className="flex items-center gap-4 min-w-0">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-medium text-primary">
            {u.name?.charAt(0)?.toUpperCase() || "?"}
          </span>
        </div>
        <div className="min-w-0">
          <p className="font-medium truncate">{u.name}</p>
          <p className="text-sm text-muted-foreground truncate">{u.email}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap pl-14 sm:pl-0">
        <Badge variant="outline" className="font-normal">{u.role}</Badge>
        <StatusBadge status={u.status} />
        {isPending && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => onApprove(u.id)} data-testid={`approve-${u.id}`}>
              <CheckCircle className="w-4 h-4 mr-1" /> Approve
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onReject(u.id)} data-testid={`reject-${u.id}`}>
              <XCircle className="w-4 h-4" />
            </Button>
          </div>
        )}
        {isInvited && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => onResendInvite(u.id)} data-testid={`resend-invite-${u.id}`}>
              <RefreshCw className="w-4 h-4 mr-1" /> Resend
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid={`invite-actions-${u.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onResendInvite(u.id)}>
                  <Mail className="w-4 h-4 mr-2" /> Resend Invitation
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-destructive" onClick={() => onRevokeInvite(u)}>
                  <Trash2 className="w-4 h-4 mr-2" /> Revoke Invitation
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
        {!isPending && !isInvited && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid={`user-actions-${u.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(u)}>
                <Edit2 className="w-4 h-4 mr-2" /> Change Role
              </DropdownMenuItem>
              {u.status !== "disabled" ? (
                <DropdownMenuItem onClick={() => onDisable(u)}>
                  <UserX className="w-4 h-4 mr-2" /> Disable Account
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => onEnable(u)}>
                  <UserCheck className="w-4 h-4 mr-2" /> Enable Account
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive" onClick={() => onDelete(u)}>
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}

function UserSection({ users, title, icon: Icon, emptyMessage, onApprove, onReject, onEdit, onDisable, onEnable, onDelete, onResendInvite, onRevokeInvite, search }) {
  const filteredUsers = users.filter(u => 
    !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-muted-foreground" />
          <CardTitle className="text-lg">{title}</CardTitle>
          <Badge variant="secondary" className="ml-auto">{filteredUsers.length}</Badge>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {filteredUsers.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">{emptyMessage}</div>
        ) : (
          <div className="divide-y">
            {filteredUsers.map(u => (
              <UserRow key={u._key || u.id} u={u} onApprove={onApprove} onReject={onReject} 
                onEdit={onEdit} onDisable={onDisable} onEnable={onEnable} onDelete={onDelete}
                onResendInvite={onResendInvite} onRevokeInvite={onRevokeInvite} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", role: "Program Manager" });
  const [sending, setSending] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [disableConfirm, setDisableConfirm] = useState(null);
  const [revokeConfirm, setRevokeConfirm] = useState(null);

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const [usersRes, pendingRes, invitesRes] = await Promise.all([
        apiClient.get("/users").catch(() => ({ data: [] })),
        apiClient.get("/users/pending").catch(() => ({ data: [] })),
        apiClient.get("/invites").catch(() => ({ data: [] })),
      ]);
      setUsers(usersRes.data || []);
      setPendingUsers(pendingRes.data || []);
      setInvites(invitesRes.data || []);
    } finally { setLoading(false); }
  }

  async function handleInvite() {
    if (!inviteForm.email || !inviteForm.name) { toast.error("Name and email required"); return; }
    setSending(true);
    try {
      await apiClient.post("/invites", inviteForm);
      toast.success("Invitation sent to " + inviteForm.email);
      setShowInvite(false);
      setInviteForm({ name: "", email: "", role: "Program Manager" });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSending(false); }
  }

  async function handleApprove(userId) {
    try { await apiClient.post("/users/" + userId + "/approve"); toast.success("Approved"); fetchData(); }
    catch (e) { toast.error("Failed"); }
  }

  async function handleReject(userId) {
    try { await apiClient.post("/users/" + userId + "/reject"); toast.success("Rejected"); fetchData(); }
    catch (e) { toast.error("Failed"); }
  }

  async function handleUpdateRole(userId, newRole) {
    try { await apiClient.put("/users/" + userId + "/role", { role: newRole }); toast.success("Updated"); setEditUser(null); fetchData(); }
    catch (e) { toast.error("Failed"); }
  }

  async function handleDisable(userId) {
    try { await apiClient.put("/users/" + userId + "/disable"); toast.success("Disabled"); setDisableConfirm(null); fetchData(); }
    catch (e) { toast.error("Failed"); }
  }

  async function handleEnable(user) {
    const userId = typeof user === "string" ? user : user?.id;
    try { await apiClient.put("/users/" + userId + "/enable"); toast.success("Enabled"); fetchData(); }
    catch (e) { toast.error("Failed"); }
  }

  async function handleDelete(userId) {
    if (!userId) {
      toast.error("Error: No user ID provided");
      console.error("Delete called with invalid userId:", userId);
      return;
    }
    try { 
      console.log("Attempting to delete user:", userId);
      await apiClient.delete("/users/" + userId); 
      toast.success("User deleted successfully"); 
      setDeleteConfirm(null); 
      fetchData(); 
    }
    catch (e) { 
      const detail = e.response?.data?.detail || e.message || "Failed to delete user";
      toast.error(detail);
      console.error("Delete user error:", {
        userId,
        status: e.response?.status,
        detail: e.response?.data,
        message: e.message
      });
    }
  }

  async function handleResendInvite(inviteId) {
    try { 
      await apiClient.post("/invites/" + inviteId + "/resend"); 
      toast.success("Invitation resent successfully"); 
      fetchData(); 
    }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to resend invitation"); }
  }

  async function handleRevokeInvite(inviteId) {
    try { 
      await apiClient.delete("/invites/" + inviteId); 
      toast.success("Invitation revoked"); 
      setRevokeConfirm(null);
      fetchData(); 
    }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to revoke invitation"); }
  }

  // Build consolidated user list with status - use unique keys to avoid React warnings
  // Only include pending invites (not expired or accepted)
  const pendingInvites = invites.filter(i => i.status === "pending");
  
  const allUsers = [
    ...users.map(u => ({ ...u, _key: `user-${u.id}`, status: u.is_disabled ? "disabled" : "active" })),
    ...pendingUsers.map(u => ({ ...u, _key: `pending-${u.id}`, status: "pending" })),
    ...pendingInvites.map(i => ({ id: i.id, _key: `invite-${i.id}`, name: i.name, email: i.email, role: i.role, status: "invited" })),
  ];

  // Filter users by category
  const adminUsers = allUsers.filter(u => ADMIN_ROLES.includes(u.role));
  const pmUsers = allUsers.filter(u => PM_ROLES.includes(u.role));
  const artistUsers = allUsers.filter(u => ARTIST_ROLES.includes(u.role));
  const pendingApproval = allUsers.filter(u => u.status === "pending");

  const commonProps = {
    onApprove: handleApprove,
    onReject: handleReject,
    onEdit: setEditUser,
    onDisable: setDisableConfirm,
    onEnable: handleEnable,
    onDelete: setDeleteConfirm,
    onResendInvite: handleResendInvite,
    onRevokeInvite: setRevokeConfirm,
    search
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light tracking-tight">User Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage team members and access permissions</p>
        </div>
        <Button onClick={() => setShowInvite(true)} className="gap-2" data-testid="invite-user-btn">
          <Plus className="w-4 h-4" /> Invite User
        </Button>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              placeholder="Search by name or email..." 
              value={search} 
              onChange={e => setSearch(e.target.value)} 
              className="pl-9" 
              data-testid="user-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Pending Approvals Alert */}
      {pendingApproval.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <CardTitle className="text-base text-amber-700">Pending Approvals ({pendingApproval.length})</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-amber-100">
              {pendingApproval.map(u => (
                <UserRow key={u.id} u={u} {...commonProps} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabbed View */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4" data-testid="user-tabs">
          <TabsTrigger value="all" className="gap-2" data-testid="tab-all">
            <Users className="w-4 h-4" />
            All ({allUsers.length})
          </TabsTrigger>
          <TabsTrigger value="admins" className="gap-2" data-testid="tab-admins">
            <Shield className="w-4 h-4" />
            Admins ({adminUsers.length})
          </TabsTrigger>
          <TabsTrigger value="pms" className="gap-2" data-testid="tab-pms">
            <Briefcase className="w-4 h-4" />
            PMs ({pmUsers.length})
          </TabsTrigger>
          <TabsTrigger value="artists" className="gap-2" data-testid="tab-artists">
            <Palette className="w-4 h-4" />
            Artists ({artistUsers.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {loading ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">Loading...</CardContent></Card>
          ) : (
            <>
              <UserSection 
                users={adminUsers} 
                title="Administrators & Staff" 
                icon={Shield}
                emptyMessage="No admin users"
                {...commonProps}
              />
              <UserSection 
                users={pmUsers} 
                title="Project Managers" 
                icon={Briefcase}
                emptyMessage="No project managers"
                {...commonProps}
              />
              <UserSection 
                users={artistUsers} 
                title="Artists" 
                icon={Palette}
                emptyMessage="No artists"
                {...commonProps}
              />
            </>
          )}
        </TabsContent>

        <TabsContent value="admins">
          {loading ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">Loading...</CardContent></Card>
          ) : (
            <UserSection 
              users={adminUsers} 
              title="Administrators & Staff" 
              icon={Shield}
              emptyMessage="No admin users found"
              {...commonProps}
            />
          )}
        </TabsContent>

        <TabsContent value="pms">
          {loading ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">Loading...</CardContent></Card>
          ) : (
            <UserSection 
              users={pmUsers} 
              title="Project Managers" 
              icon={Briefcase}
              emptyMessage="No project managers found"
              {...commonProps}
            />
          )}
        </TabsContent>

        <TabsContent value="artists">
          {loading ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">Loading...</CardContent></Card>
          ) : (
            <UserSection 
              users={artistUsers} 
              title="Artists" 
              icon={Palette}
              emptyMessage="No artists found"
              {...commonProps}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Invite Dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>Send an invitation to join RenderPros OS</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Name</label>
              <Input placeholder="Full name" value={inviteForm.name} onChange={e => setInviteForm({ ...inviteForm, name: e.target.value })} data-testid="invite-name-input" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Email</label>
              <Input type="email" placeholder="email@example.com" value={inviteForm.email} onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })} data-testid="invite-email-input" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Role</label>
              <Select value={inviteForm.role} onValueChange={v => setInviteForm({ ...inviteForm, role: v })}>
                <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvite(false)}>Cancel</Button>
            <Button onClick={handleInvite} disabled={sending} data-testid="send-invite-btn">{sending ? "Sending..." : "Send Invite"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Change Role for {editUser?.name}</DialogTitle></DialogHeader>
          <div className="py-4">
            <Select value={editUser?.role} onValueChange={v => handleUpdateRole(editUser?.id, v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </DialogContent>
      </Dialog>

      {/* Disable Confirmation */}
      <AlertDialog open={!!disableConfirm} onOpenChange={() => setDisableConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable Account</AlertDialogTitle>
            <AlertDialogDescription>{disableConfirm?.name} will lose access immediately. This can be reversed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDisable(disableConfirm?.id)}>Disable</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" /> Permanently Delete
            </AlertDialogTitle>
            <AlertDialogDescription>Delete {deleteConfirm?.name} ({deleteConfirm?.email})? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-destructive hover:bg-destructive/90" 
              onClick={() => {
                const userId = deleteConfirm?.id || deleteConfirm?._id;
                console.log("Deleting user with ID:", userId, "Full object:", deleteConfirm);
                handleDelete(userId);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke Invitation Confirmation */}
      <AlertDialog open={!!revokeConfirm} onOpenChange={() => setRevokeConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" /> Revoke Invitation
            </AlertDialogTitle>
            <AlertDialogDescription>
              Revoke the invitation for {revokeConfirm?.name} ({revokeConfirm?.email})? They will no longer be able to join using this invitation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={() => handleRevokeInvite(revokeConfirm?.id)}>Revoke</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
